import { Fragment, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import {
  apiErrorText,
  apiLoadErrorText,
  useGetVolumeFactQuery,
  useGetFieldDailyReportQuery,
  useGetFieldElementsQuery,
  useGetFieldSiteQuery,
  useVerifyVolumeFactMutation,
  useConfirmVolumeFactMutation,
  useDisputeVolumeFactMutation,
  useBindVolumeFactMutation,
} from '@shared/api';
import {
  activeFactEscalation,
  canAcceptFact,
  canBindFact,
  canConfirmFact,
  canConfirmFactBinding,
  canDisputeFact,
  canVerifyFact,
  foremanConfirmView,
  isOwnReport,
  useCurrentUserId,
  DISPUTE_COMMENT_MAX,
  disputeCommentError,
  elementStatusLabel,
  factAcceptedQty,
  factAcceptedView,
  factEventActorLabel,
  factEventDetail,
  factEventLabel,
  factNeedsReview,
  factReconcileFlags,
  factReconcileRows,
  factReconcileView,
  fmtFieldDate,
  parseQtyInput,
  siteReadOnly,
  useFieldAccess,
  useSiteForeman,
  volumeFactStatusLabel,
  volumeFactStatusTone,
  type FieldElement,
} from '@entities/field';
import { Card } from '@shared/ui/Card';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { Input } from '@shared/ui/Input';
import { Table } from '@shared/ui/Table';
import { Combobox, type ComboboxOption } from '@shared/ui/Combobox';
import { ProjectInBinBanner } from './ProjectInBinBanner';

const BackLink = styled(Link)`
  display: inline-block;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-decoration: none;
  margin-bottom: ${({ theme }) => theme.spacing(4)};

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const Title = styled.h2`
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.01em;
`;

const HeadRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
`;

const Meta = styled.div`
  font-size: 13.5px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: ${({ theme }) => theme.spacing(2)};
  line-height: 1.6;
`;

const Section = styled.section`
  margin-top: ${({ theme }) => theme.spacing(8)};
`;

const SectionLabel = styled.div`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const Muted = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
`;

const ReconcileBox = styled(Card)`
  font-size: 14px;
  line-height: 1.6;
`;

const Hint = styled.div`
  margin-top: ${({ theme }) => theme.spacing(2)};
  font-size: 13.5px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Facts = styled.dl`
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(5)};
  margin: 0;
  font-size: 14px;

  & dt {
    color: ${({ theme }) => theme.colors.text.secondary};
  }
  & dd {
    margin: 0;
    font-variant-numeric: tabular-nums;
  }
`;

const TableWrap = styled(Card)`
  padding: 0;
  overflow-x: auto;
`;

const Actions = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
  margin-top: ${({ theme }) => theme.spacing(5)};
`;

const EditBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
  flex: 1;
  min-width: 280px;
`;

const BindBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
  max-width: 560px;
  margin-top: ${({ theme }) => theme.spacing(4)};
`;

const CommentArea = styled.textarea`
  width: 100%;
  min-height: 64px;
  padding: ${({ theme }) => theme.spacing(2.5)} ${({ theme }) => theme.spacing(3.5)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: 14px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ErrorNote = styled.div.attrs({ role: 'alert' })`
  color: ${({ theme }) => theme.colors.status.danger};
  font-size: 13px;
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

const OkNote = styled.div`
  color: ${({ theme }) => theme.colors.status.success};
  font-size: 13px;
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

/** Решение принято, но сервер предупреждает (превышение РД). */
const WarnNote = styled.div.attrs({ role: 'status' })`
  color: ${({ theme }) => theme.colors.status.warning};
  font-size: 13.5px;
  line-height: 1.55;
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

const Flags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
  align-items: center;
`;

const InlineLink = styled(Link)`
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

/** Эскалация (прораб не согласен с правкой ПТО или просрочил её) — заметно,
 *  рядом с шапкой: решение за ПТО. */
const EscalationBox = styled(Card).attrs({ role: 'status' })`
  margin-top: ${({ theme }) => theme.spacing(4)};
  border-color: ${({ theme }) => theme.colors.status.warning};
  font-size: 14px;
  line-height: 1.6;
`;

const DisputeQuote = styled.div`
  margin-top: ${({ theme }) => theme.spacing(2)};
  white-space: pre-wrap;
  color: ${({ theme }) => theme.colors.text.primary};
`;

/** Счётчик символов под полем комментария. */
const Counter = styled.div<{ $over: boolean }>`
  font-size: 12px;
  color: ${({ theme, $over }) => ($over ? theme.colors.status.danger : theme.colors.text.secondary)};
`;

/** Форма «Не согласен» прораба — под кнопками подтверждения. */
const DisputeForm = styled(EditBox)`
  margin-top: ${({ theme }) => theme.spacing(3)};
  max-width: 640px;
`;

/** Число → строка с разумной точностью, null → прочерк. */
function fmtNum(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return String(Math.round(value * 1000) / 1000);
}

/** Пояснение к числу (серым, после значения). */
const Aside = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 13px;
`;

/** Подпись элемента конструктива: «код — наименование». */
function elementTitle(el: FieldElement): string {
  return el.code ? `${el.code} — ${el.title}` : el.title;
}

/** Элемент → вариант выбора: сначала элементы с позицией РД (только они дают сверку). */
function elementOption(el: FieldElement): ComboboxOption {
  const where = [el.titul, el.section, el.axes, el.marks].filter(Boolean).join(' · ');
  const rd = el.position_id ? 'с позицией РД' : 'без позиции РД — сверки не будет';
  return { id: el.id, label: elementTitle(el), sublabel: where ? `${where} · ${rd}` : rd };
}

/**
 * Карточка факта объёма (`/agents/field/:siteId/volume-facts/:factId`, §8.1):
 * заявленный объём, сверка с РД и ранее принятым (тот же вид работ, нерешённые
 * заявки, пометки сверки), автомат подтверждения. Проверяющий ПТО принимает /
 * правит / возвращает и привязывает факт к конструктиву, подтверждает привязку,
 * подобранную программой; правку подтверждает прораб площадки (SLA 2 дня) или
 * отвечает «не согласен» с комментарием — факт уходит на эскалацию, и решение
 * (принять правку, поправить, вернуть) за ПТО, как и по просроченной правке
 * (КW2). Несогласие прораба видно заметно, рядом с шапкой, и в журнале. После
 * «Не согласен» прораб правку уже не подтверждает — решение за ПТО (КR11); объём
 * своего рапорта проверяющий не сверяет, свою правку как прораб не подтверждает.
 * Подсказки по привязке к конструктиву — проверяющему, прорабу — кто её сделает.
 * Предупреждение сервера о превышении РД после решения показываем. Права решает
 * сервер — здесь только не показываем кнопки, которые ответят 403 или 409
 * (проект ПТО площадки в корзине).
 */
export function FieldFactDetail() {
  const { siteId, factId } = useParams<{ siteId: string; factId: string }>();
  const id = factId ?? '';
  const {
    data,
    isLoading,
    isError,
    error: loadError,
  } = useGetVolumeFactQuery(id, {
    skip: !factId,
  });
  const fact = data?.fact;
  const events = data?.events ?? [];
  const site = fact?.site_id ?? siteId ?? '';

  // Проект ПТО площадки в корзине — карточка только для чтения (сервер: 409).
  const { data: siteResp } = useGetFieldSiteQuery(site, { skip: !site });
  const readOnly = siteReadOnly(siteResp?.site);
  // Проверяющий — по проекту факта (глобальная роль или команда проекта ПТО).
  const access = useFieldAccess(fact?.project_id);
  const reviewer = access.reviewer && !readOnly;
  const assignedForeman = useSiteForeman(site) && !readOnly;
  const userId = useCurrentUserId();
  // Автор рапорта факта — проверяющему: объём своего рапорта он не сверяет (КR11).
  const { data: reportResp } = useGetFieldDailyReportQuery(fact?.report_id ?? '', {
    skip: !fact,
  });
  const ownReport = isOwnReport(reportResp?.report.author_id, userId);
  const foreman = assignedForeman && ownReport;
  const { data: elResp } = useGetFieldElementsQuery(site, { skip: !site });
  const [verify, { isLoading: verifying }] = useVerifyVolumeFactMutation();
  const [confirm, { isLoading: confirming }] = useConfirmVolumeFactMutation();
  const [sendDispute, { isLoading: disputing }] = useDisputeVolumeFactMutation();
  const [bind, { isLoading: binding }] = useBindVolumeFactMutation();

  const [mode, setMode] = useState<'correct' | 'reject' | null>(null);
  const [correctedQty, setCorrectedQty] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Решение принято, но объём выходит за РД — текст сервера (`warning` ответа).
  const [warning, setWarning] = useState<string | null>(null);
  // Итог действия без изменений или с пояснением (повтор подтверждения, несогласие).
  const [info, setInfo] = useState<string | null>(null);
  const [bindTo, setBindTo] = useState('');
  const [bindOk, setBindOk] = useState<string | null>(null);
  // «Не согласен» прораба с правкой ПТО: форма комментария (КW2).
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [disputeText, setDisputeText] = useState('');

  const busy = verifying || confirming || binding || disputing;
  const parsedQty = parseQtyInput(correctedQty);
  const qtyInvalid = correctedQty.trim() !== '' && parsedQty === null;

  const elements = useMemo(
    () =>
      [...(elResp?.elements ?? [])].sort(
        (a, b) => Number(!a.position_id) - Number(!b.position_id) || a.sort - b.sort,
      ),
    [elResp],
  );
  const options = useMemo(() => elements.map(elementOption), [elements]);
  const boundElement = elements.find((el) => el.id === fact?.element_id);
  const chosen = options.find((o) => o.id === bindTo);

  async function act(action: 'accept' | 'correct' | 'reject') {
    setError(null);
    setWarning(null);
    setInfo(null);
    try {
      let res: { warning?: string | null };
      if (action === 'correct') {
        // Пустое, ноль, минус и «12 шт» не уходят на сервер (раньше пустое поле
        // отправлялось нулём, а «−5» — отрицательным объёмом).
        if (parsedQty === null) {
          setError('Введите исправленный объём числом больше нуля (например, 12,5).');
          return;
        }
        res = await verify({
          id,
          action,
          corrected_qty: parsedQty,
          note: note.trim() || undefined,
        }).unwrap();
      } else if (action === 'reject') {
        res = await verify({ id, action, note: note.trim() || undefined }).unwrap();
      } else {
        res = await verify({ id, action }).unwrap();
      }
      // Возвращённый объём в итог не входит: предупреждение о превышении (сервер
      // отдаёт его и при возврате — по сверке на момент заявки) показываем только
      // у принятого или исправленного.
      setWarning(action === 'reject' ? null : res.warning?.trim() || null);
      setMode(null);
      setCorrectedQty('');
      setNote('');
    } catch (e) {
      setError(apiErrorText(e, 'Не удалось выполнить действие.'));
    }
  }

  async function confirmFact() {
    setError(null);
    setWarning(null);
    setInfo(null);
    try {
      const res = await confirm(id).unwrap();
      // Повтор по уже принятому (страница устарела) — сервер ничего не менял (КW3).
      if (res.unchanged) {
        setInfo('Объём уже подтверждён вами — изменений нет (страница была устаревшей).');
      }
      setWarning(res.warning?.trim() || null);
      setDisputeOpen(false);
    } catch (e) {
      setError(apiErrorText(e, 'Не удалось подтвердить объём.'));
    }
  }

  /** «Не согласен» с правкой ПТО: комментарий обязателен (КW2) — факт уходит на
   *  эскалацию, решение по объёму примет ПТО. */
  async function disputeFact() {
    const problem = disputeCommentError(disputeText);
    if (problem) {
      setError(problem);
      return;
    }
    setError(null);
    setWarning(null);
    setInfo(null);
    try {
      await sendDispute({ id, comment: disputeText.trim() }).unwrap();
      setDisputeOpen(false);
      setDisputeText('');
      setInfo('Несогласие отправлено ПТО — решение по объёму примет инженер ПТО.');
    } catch (e) {
      setError(apiErrorText(e, 'Не удалось отправить несогласие с правкой.'));
    }
  }

  /** Привязка к элементу: выбранному в списке или — подтверждение автопривязки —
   *  к текущему (сервер снимает пометку «подобрано автоматически»). */
  async function bindFact(elementId: string, done: string) {
    if (!elementId) return;
    setError(null);
    setBindOk(null);
    try {
      await bind({ id, element_id: elementId }).unwrap();
      setBindOk(done);
      setBindTo('');
    } catch (e) {
      setError(apiErrorText(e, 'Не удалось привязать факт к конструктиву.'));
    }
  }

  const rec = fact?.reconcile;
  // Подсказки по привязке и сверке по чертежу — проверяющему; прорабу — кто это сделает.
  const view = factReconcileView(rec, { elementBound: !!fact?.element_id, reviewer });
  const status = fact?.status ?? '';
  const flags = factReconcileFlags(rec, status, { reviewer });
  const rows = factReconcileRows(rec);
  const awaitsForeman = canConfirmFact(status);
  const acceptedView = fact ? factAcceptedView(fact) : null;
  const autoBound = !!fact && canConfirmFactBinding(fact);
  // Эскалация: прораб не согласен с правкой ПТО (КW2) или не подтвердил её в срок.
  const escalated = status === 'escalated';
  const escalation = activeFactEscalation(status, events);
  const disputed = escalation?.reason === 'foreman_disputed';
  // «Подтвердить объём» прорабом (КR11): не после «Не согласен» и не по своей правке.
  const confirmView = fact
    ? foremanConfirmView(fact, escalation, userId)
    : { allowed: false, reason: null };
  // Решает ПТО — проверяющий, но не по объёму своего же рапорта (КR11).
  const decides = reviewer && !ownReport;
  const disputeProblem = disputeOpen ? disputeCommentError(disputeText) : null;
  const disputeLen = [...disputeText.trim()].length;
  const titleOfElement = (elId: string): string | null => {
    const el = elements.find((e) => e.id === elId);
    return el ? elementTitle(el) : null;
  };

  return (
    <>
      <BackLink to={`/agents/field/${site}`}>← К площадке</BackLink>

      {isLoading && <Muted>Загрузка факта…</Muted>}
      {isError && (
        <Muted>
          {apiLoadErrorText(
            loadError,
            'Не удалось загрузить факт объёма.',
            'Нет доступа к факту объёма: он виден участникам площадки и проверяющим ПТО проекта.',
          )}
        </Muted>
      )}

      {fact && (
        <>
          <ProjectInBinBanner site={siteResp?.site} />
          <HeadRow>
            <Title>
              {fact.work_item_code ? `${fact.work_item_code} — ${fact.name}` : fact.name}
            </Title>
            <Badge $tone={volumeFactStatusTone(fact.status)}>
              {volumeFactStatusLabel(fact.status)}
            </Badge>
            {escalation && (
              <Badge $tone={disputed ? 'danger' : 'warning'}>
                {disputed ? 'прораб не согласен' : 'правка просрочена'}
              </Badge>
            )}
            {/* «Нужна проверка» — только пока по факту нет решения ПТО (B9). */}
            {factNeedsReview(fact) && <Badge $tone="warning">нужна проверка</Badge>}
          </HeadRow>
          <Meta>
            {[
              fact.titul && `Титул: ${fact.titul}`,
              fact.mark && `Марка: ${fact.mark}`,
              fact.drawing && `Чертёж: ${fact.drawing}`,
            ]
              .filter(Boolean)
              .join(' · ')}
            {(fact.titul || fact.mark || fact.drawing) && <br />}
            Создан: {fmtFieldDate(fact.created_at)} ·{' '}
            <InlineLink to={`/agents/field/${site}/reports/${fact.report_id}`}>
              рапорт смены →
            </InlineLink>
          </Meta>

          {/* КW2: прораб ответил «не согласен» на правку ПТО или просрочил её —
              эскалация ждёт решения ПТО. */}
          {escalation && (
            <EscalationBox>
              <strong>
                {disputed
                  ? 'Прораб не согласен с правкой объёма'
                  : 'Прораб не подтвердил правку ПТО в срок'}
              </strong>
              <Aside>
                {' · '}
                {fmtFieldDate(escalation.at)}
                {escalation.deadline ? ` (срок был до ${fmtFieldDate(escalation.deadline)})` : ''}
              </Aside>
              {disputed && (
                <DisputeQuote>
                  {escalation.comment ? `«${escalation.comment}»` : 'Комментария нет.'}
                </DisputeQuote>
              )}
              <Hint>
                Заявлено прорабом: {fact.qty_raw} {fact.unit ?? ''}; правка ПТО:{' '}
                {fmtNum(fact.corrected_qty)} {fact.unit ?? ''}.{' '}
                {disputed
                  ? decides
                    ? 'Решение за ПТО: примите правку, поправьте объём (новая правка уйдёт прорабу на подтверждение) или верните объём прорабу — кнопки ниже.'
                    : 'Решение по объёму примет инженер ПТО.'
                  : decides
                    ? 'Прораб ещё может подтвердить объём, а решение может принять ПТО: примите правку, поправьте объём (новая правка уйдёт прорабу на подтверждение) или верните объём прорабу — кнопки ниже.'
                    : 'Прораб ещё может подтвердить объём; решение может принять инженер ПТО.'}
              </Hint>
            </EscalationBox>
          )}

          <Section>
            <SectionLabel>Объём</SectionLabel>
            <Facts>
              <dt>Заявлено (как в рапорте)</dt>
              <dd>
                {fact.qty_raw} {fact.unit ?? ''}
              </dd>
              <dt>Распознано</dt>
              <dd>{fmtNum(fact.qty)}</dd>
              {fact.corrected_qty !== null && (
                <>
                  <dt>Правка ПТО</dt>
                  <dd>{fmtNum(fact.corrected_qty)}</dd>
                </>
              )}
              {acceptedView && (
                <>
                  <dt>{acceptedView.label}</dt>
                  <dd>
                    {acceptedView.value === null ? (
                      <Aside>{acceptedView.note ?? '—'}</Aside>
                    ) : (
                      <>
                        {fmtNum(acceptedView.value)}
                        {acceptedView.note && <Aside> — {acceptedView.note}</Aside>}
                      </>
                    )}
                  </dd>
                </>
              )}
            </Facts>
            {fact.correction_note && <Meta>Комментарий ПТО: {fact.correction_note}</Meta>}
          </Section>

          <Section>
            <SectionLabel>Сверка</SectionLabel>
            <ReconcileBox>
              <Flags>
                <Badge $tone={view.tone}>{view.label}</Badge>
                {flags.map((f) => (
                  <Badge key={f.label} $tone={f.tone} title={f.hint}>
                    {f.label}
                  </Badge>
                ))}
              </Flags>
              {/* Текст сервера — что случилось и что делать (в том числе «у элемента нет
                  позиции РД — привяжите элемент…»); подсказка веба — где это сделать. */}
              <Meta>{rec?.message ?? 'Сверка не выполнялась.'}</Meta>
              {status === 'rejected' && (
                <Hint>
                  Объём возвращён прорабу: сверка показана на момент заявки, в принятый итог позиции
                  этот объём не входит.
                </Hint>
              )}
              {view.hint && <Hint>{view.hint}</Hint>}
              {rows.length > 0 && (
                <Facts style={{ marginTop: 12 }}>
                  {rows.map((r) => (
                    <Fragment key={r.label}>
                      <dt>{r.label}</dt>
                      <dd>{r.value}</dd>
                    </Fragment>
                  ))}
                </Facts>
              )}
            </ReconcileBox>
          </Section>

          <Section>
            <SectionLabel>Конструктив</SectionLabel>
            {boundElement ? (
              <Muted>
                {elementTitle(boundElement)} · {elementStatusLabel(boundElement.status)} ·{' '}
                {boundElement.position_id ? 'с позицией РД' : 'без позиции РД'}
              </Muted>
            ) : fact.element_id ? (
              <Muted>Привязан к элементу, которого нет в конструктиве площадки (удалён?).</Muted>
            ) : (
              <Muted>
                Не привязан — из рапорта элемент не определился.
                {!reviewer && ' Привязку к конструктиву делает инженер ПТО.'}
              </Muted>
            )}
            {autoBound && (
              <Hint>
                Элемент подобран программой по наименованию работы, а не выбран человеком.
                {reviewer
                  ? ' Проверьте: если элемент верный — подтвердите привязку, иначе выберите другой ниже.'
                  : ' Привязку проверит инженер ПТО.'}
              </Hint>
            )}
            {reviewer && autoBound && fact.element_id && (
              <Actions style={{ marginTop: 12 }}>
                <Button
                  $variant="secondary"
                  disabled={busy}
                  onClick={() =>
                    void bindFact(
                      fact.element_id ?? '',
                      `Привязка подтверждена: ${
                        boundElement ? elementTitle(boundElement) : 'элемент конструктива'
                      }. Сверка пересчитана.`,
                    )
                  }
                >
                  {binding ? 'Подтверждаю…' : 'Подтвердить привязку'}
                </Button>
              </Actions>
            )}
            {reviewer && canBindFact(status) && (
              <BindBox>
                {elements.length === 0 ? (
                  <Muted>
                    На площадке нет конструктива.{' '}
                    <InlineLink to={`/agents/field/${site}`}>Добавьте его из РД</InlineLink> на
                    странице площадки, затем привяжите факт.
                  </Muted>
                ) : (
                  <>
                    <Combobox
                      id="fact-bind-element"
                      value={bindTo}
                      selectedLabel={chosen?.label}
                      options={options}
                      onChange={(o) => setBindTo(o?.id ?? '')}
                      placeholder={
                        fact.element_id
                          ? 'Перепривязать к другому элементу…'
                          : 'Выберите элемент конструктива…'
                      }
                      emptyText="Элемент не найден"
                      disabled={busy}
                    />
                    <div>
                      <Button
                        $variant="secondary"
                        disabled={busy || !bindTo || bindTo === fact.element_id}
                        onClick={() =>
                          void bindFact(
                            bindTo,
                            `Факт привязан: ${chosen?.label ?? 'элемент конструктива'}. Сверка пересчитана.`,
                          )
                        }
                      >
                        {binding ? 'Привязываю…' : 'Привязать к конструктиву'}
                      </Button>
                    </div>
                  </>
                )}
              </BindBox>
            )}
            {bindOk && <OkNote>{bindOk}</OkNote>}
          </Section>

          {reviewer && ownReport && canVerifyFact(status) && (
            <Actions>
              <Muted>
                Объём заявлен в вашем рапорте — решение по нему принимает другой проверяющий ПТО:
                заявленное и принятое не подтверждает один человек.
              </Muted>
            </Actions>
          )}
          {decides && canVerifyFact(status) && (
            <Actions>
              <Button
                $variant="primary"
                disabled={busy || !canAcceptFact(fact)}
                onClick={() => act('accept')}
                title={
                  !canAcceptFact(fact)
                    ? 'Объём не распознан — принимать нечего. Поправьте объём числом.'
                    : escalated
                      ? 'Объём станет принятым по правке ПТО без подтверждения прораба'
                      : undefined
                }
              >
                {escalated && fact.corrected_qty !== null
                  ? `Принять правку ПТО: ${fmtNum(fact.corrected_qty)} ${fact.unit ?? ''}`.trim()
                  : 'Принять как есть'}
              </Button>
              {mode !== 'correct' && (
                <Button $variant="secondary" disabled={busy} onClick={() => setMode('correct')}>
                  Поправить объём…
                </Button>
              )}
              {mode !== 'reject' && (
                <Button $variant="ghost" disabled={busy} onClick={() => setMode('reject')}>
                  Вернуть…
                </Button>
              )}
              {mode === null && !canAcceptFact(fact) && (
                <Muted>
                  Объём не распознан — принимать нечего: поправьте его числом или верните прорабу.
                </Muted>
              )}
              {mode === 'correct' && (
                <EditBox>
                  <Input
                    value={correctedQty}
                    onChange={(e) => setCorrectedQty(e.target.value)}
                    placeholder={`Исправленный объём${
                      fact.unit ? `, ${fact.unit}` : ''
                    } (число больше нуля)`}
                    inputMode="decimal"
                    aria-label="Исправленный объём"
                    aria-invalid={qtyInvalid}
                  />
                  {qtyInvalid && (
                    <ErrorNote style={{ marginTop: 0 }}>
                      Нужно число больше нуля: «12», «12,5» или «1 250».
                    </ErrorNote>
                  )}
                  <CommentArea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Комментарий прорабу: почему изменён объём"
                    aria-label="Комментарий к правке"
                  />
                  <Actions style={{ marginTop: 0 }}>
                    <Button
                      $variant="primary"
                      disabled={busy || parsedQty === null}
                      onClick={() => act('correct')}
                    >
                      Отправить прорабу на подтверждение
                    </Button>
                    <Button $variant="ghost" disabled={busy} onClick={() => setMode(null)}>
                      Отмена
                    </Button>
                  </Actions>
                </EditBox>
              )}
              {mode === 'reject' && (
                <EditBox>
                  <CommentArea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Причина возврата"
                    aria-label="Причина возврата"
                  />
                  <Actions style={{ marginTop: 0 }}>
                    <Button $variant="danger" disabled={busy} onClick={() => act('reject')}>
                      Вернуть объём
                    </Button>
                    <Button $variant="ghost" disabled={busy} onClick={() => setMode(null)}>
                      Отмена
                    </Button>
                  </Actions>
                </EditBox>
              )}
            </Actions>
          )}
          {!reviewer && status === 'declared' && (
            <Actions>
              <Muted>Объём на сверке у инженера ПТО.</Muted>
            </Actions>
          )}

          {awaitsForeman &&
            (foreman && !confirmView.allowed ? (
              // КR11: после «Не согласен» решение за ПТО, а свою же правку (проверяющий
              // и прораб — один человек) как прораб не подтверждают.
              confirmView.reason && (
                <Actions>
                  <Muted>{confirmView.reason}</Muted>
                </Actions>
              )
            ) : foreman ? (
              <>
                <Actions>
                  <Button $variant="primary" disabled={busy} onClick={confirmFact}>
                    Подтвердить объём
                  </Button>
                  {canDisputeFact(status) && !disputeOpen && (
                    <Button
                      $variant="secondary"
                      disabled={busy}
                      onClick={() => {
                        setError(null);
                        setInfo(null);
                        setDisputeOpen(true);
                      }}
                    >
                      Не согласен…
                    </Button>
                  )}
                  <Muted>
                    {escalated
                      ? 'Срок подтверждения правки истёк, но подтвердить объём ещё можно: '
                      : 'Правку внёс ПТО. Подтвердите принятый объём '}
                    {fmtNum(factAcceptedQty(fact))} {fact.unit ?? ''}
                    {escalated ? '.' : ' — во избежание разночтений.'}
                  </Muted>
                </Actions>
                {disputeOpen && canDisputeFact(status) && (
                  <DisputeForm>
                    <CommentArea
                      value={disputeText}
                      onChange={(e) => setDisputeText(e.target.value)}
                      placeholder="С чем вы не согласны: какой объём выполнен на самом деле и почему"
                      aria-label="Комментарий: с чем не согласны"
                      aria-invalid={disputeText.trim() !== '' && !!disputeProblem}
                    />
                    <Counter $over={disputeLen > DISPUTE_COMMENT_MAX}>
                      {disputeLen} / {DISPUTE_COMMENT_MAX}
                    </Counter>
                    <Actions style={{ marginTop: 0 }}>
                      <Button
                        $variant="primary"
                        disabled={busy || !!disputeProblem}
                        title={disputeProblem ?? undefined}
                        onClick={() => void disputeFact()}
                      >
                        {disputing ? 'Отправляю…' : 'Отправить ПТО'}
                      </Button>
                      <Button
                        $variant="ghost"
                        disabled={busy}
                        onClick={() => {
                          setDisputeOpen(false);
                          setDisputeText('');
                        }}
                      >
                        Отмена
                      </Button>
                    </Actions>
                  </DisputeForm>
                )}
              </>
            ) : (
              // Эскалацию объясняет блок под шапкой; здесь — только ожидание прораба.
              !escalated && (
                <Actions>
                  <Muted>
                    Правка ПТО ждёт подтверждения прораба площадки
                    {fact.foreman_deadline
                      ? ` (срок — ${fmtFieldDate(fact.foreman_deadline)})`
                      : ''}
                    . Подтвердить объём за прораба нельзя.
                  </Muted>
                </Actions>
              )
            ))}

          {error && <ErrorNote>{error}</ErrorNote>}
          {info && <OkNote>{info}</OkNote>}
          {warning && (
            <WarnNote>
              <strong>Решение сохранено, но объём выходит за РД.</strong> {warning}
            </WarnNote>
          )}

          <Section>
            <SectionLabel>Журнал</SectionLabel>
            {events.length === 0 ? (
              <Muted>Событий нет.</Muted>
            ) : (
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <th>Событие</th>
                      <th>Подробности</th>
                      <th>Кто</th>
                      <th>Статус</th>
                      <th>Когда</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((ev) => (
                      <tr key={ev.id}>
                        <td>{factEventLabel(ev)}</td>
                        <td>{factEventDetail(ev, titleOfElement) || '—'}</td>
                        <td>{factEventActorLabel(ev.actor_role)}</td>
                        <td>{volumeFactStatusLabel(ev.to_status)}</td>
                        <td>{fmtFieldDate(ev.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
            )}
          </Section>
        </>
      )}
    </>
  );
}
